import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface Datastream {
  id: string;
  name: string;
  stream_description: string | null;
  update_frequency: string | null;
  datavolume: string | null;
}

interface Source {
  id: string;
  name: string;
  description: string | null;
  datastreams: Datastream[];
}

export default function Index() {
  const [sources, setSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(true);
  const [openSource, setOpenSource] = useState<string | null>(null);
  const [openDatastream, setOpenDatastream] = useState<string | null>(null);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  const sourceRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const datastreamRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    loadSourcesWithDatastreams();
    
    // Check if we should show welcome dialog
    const shouldShowWelcome = sessionStorage.getItem('showWelcome');
    if (shouldShowWelcome === 'true') {
      setShowWelcomeDialog(true);
      sessionStorage.removeItem('showWelcome');
    }
  }, []);

  const loadSourcesWithDatastreams = async () => {
    try {
      setLoading(true);

      const { data: sourcesData, error: sourcesError } = await supabase
        .from("sources")
        .select(`
          id,
          name,
          description,
          display_order,
          datastreams (
            id,
            name,
            stream_description,
            update_frequency,
            datavolume
          )
        `)
        .eq("status", "active")
        .order("display_order", { ascending: true });

      if (sourcesError) throw sourcesError;

      setSources(sourcesData || []);
    } catch (error) {
      console.error("Error loading sources:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSourceChange = (value: string | undefined) => {
    setOpenSource(value || null);
    if (value && sourceRefs.current[value]) {
      setTimeout(() => {
        const element = sourceRefs.current[value];
        if (element) {
          const yOffset = -160;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handleDatastreamChange = (value: string | undefined) => {
    setOpenDatastream(value || null);
    if (value && datastreamRefs.current[value]) {
      setTimeout(() => {
        const element = datastreamRefs.current[value];
        if (element) {
          const yOffset = -160;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handleAddToCart = (datastream: Datastream, source: Source) => {
    if (!user) {
      navigate('/auth');
      return;
    }
    
    addToCart({
      datastreamId: datastream.id,
      datastreamName: datastream.name,
      sourceId: source.id,
      sourceName: source.name,
      sourceDescription: source.description,
      subscriptionType: null,
      primaryUseCase: null,
      updateFrequency: datastream.update_frequency,
    });
    toast.success('Toegevoegd aan winkelmandje');
    navigate('/catalogus');
  };

  if (loading) {
  return (
    <div className="container mx-auto px-4 py-8">
      <Dialog open={showWelcomeDialog} onOpenChange={setShowWelcomeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Welkom!</DialogTitle>
            <DialogDescription className="text-base pt-4">
              Bekijk de beschikbare bronnen die Profiteia aanbiedt. Op de index pagina vind je een overzicht van alle bronnen en datastromen. Je kunt hier starten met een aanvraag. Via de catalogus kun je alle relevante bronnen toevoegen voor jouw aanvraag.
            </DialogDescription>
          </DialogHeader>
          <Button onClick={() => setShowWelcomeDialog(false)}>
            Ga door
          </Button>
        </DialogContent>
      </Dialog>

      <h1 className="text-3xl font-bold mb-6">Index</h1>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-2">Index</h1>
      <p className="text-muted-foreground mb-6">
        Overzicht van alle beschikbare databronnen en hun datastromen.
      </p>

      {sources.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Geen databronnen beschikbaar.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <Accordion 
            type="single" 
            collapsible
            value={openSource || undefined}
            onValueChange={handleSourceChange}
          >
            {sources.map((source) => (
              <AccordionItem 
                key={source.id} 
                value={source.id}
                ref={(el) => (sourceRefs.current[source.id] = el)}
              >
                <AccordionTrigger className="hover:no-underline text-lg font-semibold">
                  {source.name} ({source.datastreams.length})
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pt-2 space-y-4">
                    {source.description && (
                      <p className="text-sm text-muted-foreground mb-4">
                        {source.description}
                      </p>
                    )}

                    {source.datastreams && source.datastreams.length > 0 ? (
                      <Accordion
                        type="single"
                        collapsible
                        value={openDatastream || undefined}
                        onValueChange={handleDatastreamChange}
                      >
                        {source.datastreams.map((datastream) => (
                          <AccordionItem 
                            key={datastream.id} 
                            value={datastream.id} 
                            className="border-l-2 pl-4"
                            ref={(el) => (datastreamRefs.current[datastream.id] = el)}
                          >
                            <AccordionTrigger className="hover:no-underline text-base">
                              {datastream.name}
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="pt-2 space-y-3">
                                {datastream.stream_description && (
                                  <div>
                                    <h4 className="font-semibold text-sm mb-1">Beschrijving</h4>
                                    <p className="text-sm text-muted-foreground">
                                      {datastream.stream_description}
                                    </p>
                                  </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {datastream.update_frequency && (
                                    <div>
                                      <h4 className="font-semibold text-sm mb-1">Update frequentie</h4>
                                      <p className="text-sm text-muted-foreground">
                                        {datastream.update_frequency}
                                      </p>
                                    </div>
                                  )}
                                  {datastream.datavolume && (
                                    <div>
                                      <h4 className="font-semibold text-sm mb-1">Datavolume</h4>
                                      <p className="text-sm text-muted-foreground">
                                        {datastream.datavolume}
                                      </p>
                                    </div>
                                  )}
                                </div>

                                <div className="pt-3">
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => handleAddToCart(datastream, source)}
                                  >
                                    Aanvraag starten
                                  </Button>
                                </div>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    ) : (
                      <p className="text-sm text-muted-foreground pl-4">
                        Geen datastromen beschikbaar voor deze bron.
                      </p>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}
    </div>
  );
}
